import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

export default function PrivacyPage() {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';

  return (
    <>
      <SEO page="privacy" />

      <div className="min-h-screen bg-slate-50 pt-20">
        <section className="bg-gradient-to-br from-orange-500 to-orange-600 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {isSpanish ? 'Política de Privacidad' : 'Privacy Policy'}
            </h1>
            <p className="text-orange-100">
              {isSpanish ? 'Última actualización: Febrero 2026' : 'Last updated: February 2026'}
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 prose prose-slate max-w-none">
            
            {isSpanish ? (
              <>
                <h2>1. Introducción</h2>
                <p>
                  Bienvenido a Hambre ("nosotros" o "nuestro"). Estamos comprometidos a proteger tu privacidad y 
                  garantizar la seguridad de tu información personal. Esta Política de Privacidad explica cómo recopilamos, 
                  usamos, divulgamos y protegemos tu información cuando utilizas nuestra plataforma de entrega de alimentos 
                  y servicios, incluyendo nuestro Sistema Operativo para Restaurantes (Hambre OS).
                </p>
                <p>
                  Esta política cumple con el Reglamento General de Protección de Datos del Reino Unido (UK GDPR), 
                  el Reglamento General de Protección de Datos de la UE (EU GDPR) y la Ley de Protección de Datos de 2018.
                </p>

                <h2>2. Información que Recopilamos</h2>
                <h3>2.1 Información que Proporcionas</h3>
                <ul>
                  <li><strong>Información de Cuenta:</strong> Nombre, dirección de correo electrónico, número de teléfono y contraseña cuando creas una cuenta.</li>
                  <li><strong>Información de Entrega:</strong> Direcciones de entrega, datos de contacto e instrucciones especiales.</li>
                  <li><strong>Información de Pago:</strong> Los datos de la tarjeta de pago se procesan de forma segura por nuestro proveedor de pagos (Stripe) y no se almacenan en nuestros servidores.</li>
                  <li><strong>Datos de Comunicación:</strong> Mensajes que envías al soporte al cliente, restaurantes o repartidores.</li>
                  <li><strong>Datos del Restaurante:</strong> Para socios restauranteros usando Hambre OS, recopilamos información del menú, datos de pedidos, información de inventario, configuración del programa de fidelidad y datos analíticos del negocio.</li>
                </ul>

                <h3>2.2 Información Recopilada Automáticamente</h3>
                <ul>
                  <li><strong>Información del Dispositivo:</strong> Tipo de dispositivo, sistema operativo, tipo de navegador y dirección IP.</li>
                  <li><strong>Datos de Uso:</strong> Páginas visitadas, funciones utilizadas, historial de pedidos y patrones de interacción.</li>
                  <li><strong>Datos de Ubicación:</strong> Con tu permiso, recopilamos datos de ubicación para mostrar restaurantes cercanos y permitir el seguimiento de entregas.</li>
                  <li><strong>Cookies:</strong> Usamos cookies y tecnologías similares para mejorar tu experiencia y analizar el uso del servicio.</li>
                </ul>

                <h2>3. Cómo Usamos Tu Información</h2>
                <p>Usamos tu información para:</p>
                <ul>
                  <li>Procesar y entregar tus pedidos</li>
                  <li>Comunicarnos contigo sobre pedidos, actualizaciones y promociones</li>
                  <li>Mejorar nuestros servicios y experiencia de usuario</li>
                  <li>Detectar y prevenir fraude o abuso</li>
                  <li>Cumplir con obligaciones legales</li>
                  <li>Personalizar tu experiencia y proporcionar recomendaciones</li>
                  <li>Proporcionar funciones de Hambre OS incluyendo gestión de inventario, programas de fidelidad e informes de fin de día</li>
                  <li>Habilitar funciones de impresión y conectividad de dispositivos para socios restauranteros</li>
                </ul>

                <h2>4. Compartir Información</h2>
                <p>Podemos compartir tu información con:</p>
                <ul>
                  <li><strong>Restaurantes:</strong> Para cumplir con tus pedidos.</li>
                  <li><strong>Socios de Entrega:</strong> Para entregar tus pedidos.</li>
                  <li><strong>Procesadores de Pago:</strong> Para procesar pagos de forma segura.</li>
                  <li><strong>Proveedores de Servicios:</strong> Que nos ayudan a operar nuestra plataforma.</li>
                  <li><strong>Requisitos Legales:</strong> Cuando lo exige la ley o para proteger nuestros derechos.</li>
                </ul>
                <p>Nunca vendemos tu información personal a terceros.</p>

                <h2>5. Verificación de Identidad y Cumplimiento de Repartidores</h2>
                
                <h3>5.1 Base Legal para el Procesamiento</h3>
                <p>
                  Hambre procesa la documentación de repartidores bajo las siguientes bases legales según el Artículo 6 del UK GDPR / EU GDPR:
                </p>
                <ul>
                  <li><strong>Obligación Legal (Art. 6(1)(c))</strong> – para cumplir con las leyes aplicables de transporte, empleo y regulación.</li>
                  <li><strong>Intereses Legítimos (Art. 6(1)(f))</strong> – para proteger la seguridad de los usuarios, mitigar el fraude y gestionar el riesgo de la plataforma.</li>
                  <li><strong>Necesidad Contractual (Art. 6(1)(b))</strong> – para permitir que los Repartidores proporcionen servicios de entrega a través de la plataforma.</li>
                </ul>

                <h3>5.2 Datos Recopilados para Verificación</h3>
                <p>Se puede requerir que los repartidores envíen:</p>
                <ul>
                  <li>Documentación de permiso de conducir</li>
                  <li>Prueba de seguro de vehículo</li>
                  <li>Documentos de registro de vehículo</li>
                  <li>Documentación de derecho a trabajar</li>
                  <li>Otra documentación legalmente requerida</li>
                </ul>

                <h3>5.3 Política de Minimización de Datos y Retención</h3>
                <p>De acuerdo con los principios de minimización de datos del GDPR:</p>
                <ul>
                  <li>Hambre revisa la documentación enviada únicamente con fines de verificación.</li>
                  <li><strong>Las copias de los documentos originales (incluyendo escaneos, imágenes y PDFs) no se retienen después de la revisión.</strong></li>
                  <li>Una vez completada la verificación, los archivos subidos se eliminan permanentemente de los sistemas activos.</li>
                </ul>

                <h3>5.4 Metadatos Retenidos Después de la Verificación</h3>
                <p>Después de una revisión exitosa, Hambre retiene solo metadatos de verificación limitados, incluyendo:</p>
                <ul>
                  <li>Tipo de documento</li>
                  <li>Estado de verificación</li>
                  <li>Fecha de vencimiento</li>
                  <li>Fecha de verificación</li>
                  <li>Referencia interna o registro de cumplimiento</li>
                </ul>
                <p><strong>No se retienen copias completas de documentos a menos que sea legalmente requerido.</strong></p>

                <h2>6. Datos de Hambre OS para Restaurantes</h2>
                <p>
                  Para socios restauranteros que usan nuestro Sistema Operativo para Restaurantes (Hambre OS), recopilamos y procesamos datos adicionales:
                </p>
                <ul>
                  <li><strong>Datos de Inventario:</strong> Niveles de stock, alertas de stock bajo y disponibilidad de artículos del menú</li>
                  <li><strong>Datos del Programa de Fidelidad:</strong> Puntos de clientes, configuración de recompensas e historial de canjes</li>
                  <li><strong>Informes de Fin de Día:</strong> Resúmenes de ventas, desglose de métodos de pago, datos de anulaciones y artículos más vendidos</li>
                  <li><strong>Configuración de Impresoras:</strong> Configuraciones de impresoras de red, USB y Bluetooth (no se almacenan datos sensibles)</li>
                  <li><strong>Datos del Sistema POS:</strong> Historial de transacciones, información de turnos y registros de caja</li>
                  <li><strong>Datos de la Pantalla de Cocina:</strong> Tiempos de pedidos, métricas de rendimiento de estaciones y flujo de trabajo de tickets</li>
                </ul>
                <p>
                  Estos datos se procesan para proporcionar las funciones de Hambre OS y se retienen según lo necesario para fines comerciales y requisitos legales de retención.
                </p>

                <h2>7. Seguridad de Datos</h2>
                <p>
                  Implementamos medidas técnicas y organizativas apropiadas para proteger tu información personal. 
                  Estas salvaguardas incluyen:
                </p>
                <ul>
                  <li>Almacenamiento y transmisión encriptados (TLS/SSL)</li>
                  <li>Controles de acceso restringido</li>
                  <li>Protocolos de eliminación segura</li>
                  <li>Registro de auditoría</li>
                  <li>Auditorías de seguridad regulares</li>
                </ul>
                <p>
                  Sin embargo, ningún método de transmisión por internet es 100% seguro, y no podemos garantizar seguridad absoluta.
                </p>

                <h2>8. Tus Derechos Bajo el GDPR</h2>
                <p>Bajo las leyes de protección de datos aplicables, tienes derecho a:</p>
                <ul>
                  <li><strong>Acceso</strong> – Solicitar una copia de los datos personales que tenemos sobre ti</li>
                  <li><strong>Rectificación</strong> – Solicitar la corrección de datos inexactos o incompletos</li>
                  <li><strong>Eliminación</strong> – Solicitar la eliminación de tus datos (sujeto a requisitos legales)</li>
                  <li><strong>Restricción</strong> – Solicitar la restricción del procesamiento en ciertas circunstancias</li>
                  <li><strong>Portabilidad</strong> – Solicitar tus datos en un formato portátil y legible por máquina</li>
                  <li><strong>Oposición</strong> – Oponerte al procesamiento basado en intereses legítimos</li>
                  <li><strong>Retirar Consentimiento</strong> – Retirar el consentimiento en cualquier momento cuando el procesamiento se basa en el consentimiento</li>
                </ul>
                <p>
                  Para ejercer estos derechos, por favor contáctanos en <a href="mailto:privacy@hambre.app">privacy@hambre.app</a>.
                </p>

                <h2>9. Retención de Datos</h2>
                <p>
                  Retenemos tu información personal durante el tiempo necesario para proporcionar nuestros servicios y cumplir 
                  con las obligaciones legales. Períodos de retención específicos:
                </p>
                <ul>
                  <li><strong>Datos de cuenta:</strong> Retenidos hasta que solicites la eliminación de tu cuenta</li>
                  <li><strong>Historial de pedidos:</strong> Retenido por 7 años para cumplimiento fiscal y legal</li>
                  <li><strong>Metadatos de verificación de repartidores:</strong> Retenidos durante la duración de la relación con el repartidor más cualquier período de retención legalmente requerido</li>
                  <li><strong>Registros de comunicación:</strong> Retenidos por 3 años o según lo exija la ley</li>
                  <li><strong>Datos de Hambre OS:</strong> Retenidos durante la duración de la asociación con el restaurante más los requisitos legales de retención aplicables</li>
                </ul>

                <h2>10. Política de Cookies</h2>
                <p>
                  Usamos cookies esenciales para operar nuestra plataforma, cookies de análisis para entender el uso y cookies 
                  de preferencias para recordar tu configuración. Puedes gestionar las preferencias de cookies a través de la configuración de tu navegador.
                </p>

                <h2>11. Privacidad de Menores</h2>
                <p>
                  Nuestros servicios no están destinados a menores de 16 años. No recopilamos conscientemente información de 
                  menores. Si nos enteramos de que hemos recopilado dicha información, la eliminaremos rápidamente.
                </p>

                <h2>12. Transferencias Internacionales</h2>
                <p>
                  Tu información puede ser transferida y procesada en países fuera del Reino Unido. Nos aseguramos de que existan 
                  salvaguardas apropiadas para proteger tus datos de acuerdo con las leyes de protección de datos aplicables, incluyendo:
                </p>
                <ul>
                  <li>Cláusulas Contractuales Estándar (SCCs)</li>
                  <li>Decisiones de adecuación cuando corresponda</li>
                  <li>Normas Corporativas Vinculantes cuando corresponda</li>
                </ul>

                <h2>13. Cambios a Esta Política</h2>
                <p>
                  Podemos actualizar esta Política de Privacidad de vez en cuando. Te notificaremos de cualquier cambio material 
                  publicando la nueva política en nuestra plataforma y actualizando la fecha de "Última actualización".
                </p>

                <h2>14. Oficial de Protección de Datos</h2>
                <p>
                  Para asuntos de protección de datos, puedes contactar a nuestro equipo de Protección de Datos:
                </p>
                <ul>
                  <li>Correo electrónico: <a href="mailto:privacy@hambre.app">privacy@hambre.app</a></li>
                  <li>Dirección: Hambre Ltd, Protección de Datos, Londres, Reino Unido</li>
                </ul>

                <h2>15. Quejas</h2>
                <p>
                  Si tienes preocupaciones sobre cómo manejamos tus datos personales, tienes derecho a presentar una queja ante 
                  la Oficina del Comisionado de Información (ICO):
                </p>
                <ul>
                  <li>Sitio web: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a></li>
                  <li>Línea de ayuda: 0303 123 1113</li>
                </ul>

                <h2>16. Contáctanos</h2>
                <p>
                  Si tienes preguntas sobre esta Política de Privacidad o nuestras prácticas de datos, por favor contáctanos:
                </p>
                <ul>
                  <li>Correo electrónico: <a href="mailto:privacy@hambre.app">privacy@hambre.app</a></li>
                  <li>Dirección: Hambre Ltd, Londres, Reino Unido</li>
                </ul>
              </>
            ) : (
              <>
                <h2>1. Introduction</h2>
                <p>
                  Welcome to Hambre ("we," "our," or "us"). We are committed to protecting your privacy and ensuring 
                  the security of your personal information. This Privacy Policy explains how we collect, use, disclose, 
                  and safeguard your information when you use our food delivery platform and services, including our 
                  Restaurant Operating System (Hambre OS).
                </p>
                <p>
                  This policy is compliant with the UK General Data Protection Regulation (UK GDPR), the EU General Data 
                  Protection Regulation (EU GDPR), and the Data Protection Act 2018.
                </p>

                <h2>2. Information We Collect</h2>
                <h3>2.1 Information You Provide</h3>
                <ul>
                  <li><strong>Account Information:</strong> Name, email address, phone number, and password when you create an account.</li>
                  <li><strong>Delivery Information:</strong> Delivery addresses, contact details, and special instructions.</li>
                  <li><strong>Payment Information:</strong> Payment card details are processed securely by our payment provider (Stripe) and are not stored on our servers.</li>
                  <li><strong>Communication Data:</strong> Messages you send to customer support, restaurants, or drivers.</li>
                  <li><strong>Restaurant Data:</strong> For restaurant partners using Hambre OS, we collect menu information, order data, inventory information, loyalty program configuration, and business analytics data.</li>
                </ul>

                <h3>2.2 Information Collected Automatically</h3>
                <ul>
                  <li><strong>Device Information:</strong> Device type, operating system, browser type, and IP address.</li>
                  <li><strong>Usage Data:</strong> Pages visited, features used, order history, and interaction patterns.</li>
                  <li><strong>Location Data:</strong> With your permission, we collect location data to show nearby restaurants and enable delivery tracking.</li>
                  <li><strong>Cookies:</strong> We use cookies and similar technologies to improve your experience and analyse service usage.</li>
                </ul>

                <h2>3. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul>
                  <li>Process and deliver your orders</li>
                  <li>Communicate with you about orders, updates, and promotions</li>
                  <li>Improve our services and user experience</li>
                  <li>Detect and prevent fraud or abuse</li>
                  <li>Comply with legal obligations</li>
                  <li>Personalise your experience and provide recommendations</li>
                  <li>Provide Hambre OS features including inventory management, loyalty programs, and end-of-day reports</li>
                  <li>Enable printing and device connectivity features for restaurant partners</li>
                </ul>

                <h2>4. Information Sharing</h2>
                <p>We may share your information with:</p>
                <ul>
                  <li><strong>Restaurants:</strong> To fulfil your orders.</li>
                  <li><strong>Delivery Partners:</strong> To deliver your orders.</li>
                  <li><strong>Payment Processors:</strong> To process payments securely.</li>
                  <li><strong>Service Providers:</strong> Who assist us in operating our platform.</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights.</li>
                </ul>
                <p>We never sell your personal information to third parties.</p>

                <h2>5. Driver Identity & Compliance Verification</h2>
                
                <h3>5.1 Legal Basis for Processing</h3>
                <p>
                  Hambre processes driver documentation under the following lawful bases pursuant to Article 6 of the UK GDPR / EU GDPR:
                </p>
                <ul>
                  <li><strong>Legal Obligation (Art. 6(1)(c))</strong> – to comply with applicable transport, employment, and regulatory laws.</li>
                  <li><strong>Legitimate Interests (Art. 6(1)(f))</strong> – to protect the safety of users, mitigate fraud, and manage platform risk.</li>
                  <li><strong>Contractual Necessity (Art. 6(1)(b))</strong> – to enable Drivers to provide delivery services through the platform.</li>
                </ul>

                <h3>5.2 Data Collected for Verification</h3>
                <p>Drivers may be required to submit:</p>
                <ul>
                  <li>Driving licence documentation</li>
                  <li>Proof of motor insurance</li>
                  <li>Vehicle registration documents</li>
                  <li>Right-to-work documentation</li>
                  <li>Other legally required documentation</li>
                </ul>

                <h3>5.3 Data Minimisation & Retention Policy</h3>
                <p>In accordance with GDPR data minimisation principles:</p>
                <ul>
                  <li>Hambre reviews submitted documentation solely for verification purposes.</li>
                  <li><strong>Copies of original documents (including scans, images, and PDFs) are not retained after review.</strong></li>
                  <li>Once verification is complete, uploaded files are permanently deleted from active systems.</li>
                </ul>

                <h3>5.4 Metadata Retained After Verification</h3>
                <p>After successful review, Hambre retains only limited verification metadata, including:</p>
                <ul>
                  <li>Document type</li>
                  <li>Verification status</li>
                  <li>Expiry date</li>
                  <li>Date of verification</li>
                  <li>Internal reference or compliance record</li>
                </ul>
                <p><strong>No full document copies are retained unless legally required.</strong></p>

                <h2>6. Hambre OS Restaurant Data</h2>
                <p>
                  For restaurant partners using our Restaurant Operating System (Hambre OS), we collect and process additional data:
                </p>
                <ul>
                  <li><strong>Inventory Data:</strong> Stock levels, low-stock alerts, and menu item availability</li>
                  <li><strong>Loyalty Program Data:</strong> Customer points, reward configurations, and redemption history</li>
                  <li><strong>End-of-Day Reports:</strong> Sales summaries, payment method breakdowns, void data, and top-selling items</li>
                  <li><strong>Printer Settings:</strong> Network, USB, and Bluetooth printer configurations (no sensitive data stored)</li>
                  <li><strong>POS System Data:</strong> Transaction history, shift information, and till records</li>
                  <li><strong>Kitchen Display Data:</strong> Order timings, station performance metrics, and ticket workflow</li>
                </ul>
                <p>
                  This data is processed to provide Hambre OS features and is retained as necessary for business purposes and legal retention requirements.
                </p>

                <h2>7. Data Security</h2>
                <p>
                  We implement appropriate technical and organisational measures to protect your personal information. 
                  These safeguards include:
                </p>
                <ul>
                  <li>Encrypted storage and transmission (TLS/SSL)</li>
                  <li>Restricted access controls</li>
                  <li>Secure deletion protocols</li>
                  <li>Audit logging</li>
                  <li>Regular security audits</li>
                </ul>
                <p>
                  However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                </p>

                <h2>8. Your Rights Under GDPR</h2>
                <p>Under applicable data protection laws, you have the right to:</p>
                <ul>
                  <li><strong>Access</strong> – Request a copy of the personal data we hold about you</li>
                  <li><strong>Rectification</strong> – Request correction of inaccurate or incomplete data</li>
                  <li><strong>Erasure</strong> – Request deletion of your data (subject to legal requirements)</li>
                  <li><strong>Restriction</strong> – Request restriction of processing in certain circumstances</li>
                  <li><strong>Portability</strong> – Request your data in a portable, machine-readable format</li>
                  <li><strong>Object</strong> – Object to processing based on legitimate interests</li>
                  <li><strong>Withdraw Consent</strong> – Withdraw consent at any time where processing is based on consent</li>
                </ul>
                <p>
                  To exercise these rights, please contact us at <a href="mailto:privacy@hambre.app">privacy@hambre.app</a>.
                </p>

                <h2>9. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary to provide our services and comply with 
                  legal obligations. Specific retention periods:
                </p>
                <ul>
                  <li><strong>Account data:</strong> Retained until you request deletion of your account</li>
                  <li><strong>Order history:</strong> Retained for 7 years for tax and legal compliance</li>
                  <li><strong>Driver verification metadata:</strong> Retained for the duration of the driver relationship plus any legally required retention period</li>
                  <li><strong>Communication records:</strong> Retained for 3 years or as required by law</li>
                  <li><strong>Hambre OS data:</strong> Retained for the duration of the restaurant partnership plus applicable legal retention requirements</li>
                </ul>

                <h2>10. Cookies Policy</h2>
                <p>
                  We use essential cookies to operate our platform, analytics cookies to understand usage, and preference 
                  cookies to remember your settings. You can manage cookie preferences through your browser settings.
                </p>

                <h2>11. Children's Privacy</h2>
                <p>
                  Our services are not intended for children under 16. We do not knowingly collect information from 
                  children. If we learn we have collected such information, we will delete it promptly.
                </p>

                <h2>12. International Transfers</h2>
                <p>
                  Your information may be transferred to and processed in countries outside the UK. We ensure appropriate 
                  safeguards are in place to protect your data in accordance with applicable data protection laws, including:
                </p>
                <ul>
                  <li>Standard Contractual Clauses (SCCs)</li>
                  <li>Adequacy decisions where applicable</li>
                  <li>Binding Corporate Rules where applicable</li>
                </ul>

                <h2>13. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by 
                  posting the new policy on our platform and updating the "Last updated" date.
                </p>

                <h2>14. Data Protection Officer</h2>
                <p>
                  For data protection matters, you may contact our Data Protection team:
                </p>
                <ul>
                  <li>Email: <a href="mailto:privacy@hambre.app">privacy@hambre.app</a></li>
                  <li>Address: Hambre Ltd, Data Protection, London, United Kingdom</li>
                </ul>

                <h2>15. Complaints</h2>
                <p>
                  If you have concerns about how we handle your personal data, you have the right to lodge a complaint with 
                  the Information Commissioner's Office (ICO):
                </p>
                <ul>
                  <li>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a></li>
                  <li>Helpline: 0303 123 1113</li>
                </ul>

                <h2>16. Contact Us</h2>
                <p>
                  If you have questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <ul>
                  <li>Email: <a href="mailto:privacy@hambre.app">privacy@hambre.app</a></li>
                  <li>Address: Hambre Ltd, London, United Kingdom</li>
                </ul>
              </>
            )}

          </div>
        </div>
      </div>
    </>
  );
}
